import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Eye, EyeOff, RefreshCw, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAdminClientsGetAdminClients } from '@/lib/api/admin-clients/admin-clients';
import {
  getAdminSecretsGetAdminClientsIdGuidSecretsQueryKey,
  useAdminSecretsDeleteAdminClientsIdGuidSecretsSecretIdGuid,
  useAdminSecretsGetAdminClientsIdGuidSecrets,
  useAdminSecretsPostAdminClientsIdGuidSecretsRotate,
} from '@/lib/api/admin-secrets/admin-secrets';
import type { ClientSecretDto, RotateSecretResult } from '@/lib/api/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { useHasRole } from '@/lib/auth/useAuth';

export default function ApiKeysPage() {
  const canManage = useHasRole('Admin');
  const [clientId, setClientId] = useState<string>('');
  const [rotateOpen, setRotateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ClientSecretDto | null>(null);

  const clientsQuery = useAdminClientsGetAdminClients({ page: 1, limit: 100 });
  const secretsQuery = useAdminSecretsGetAdminClientsIdGuidSecrets(clientId, {
    query: { enabled: !!clientId },
  });

  return (
    <section className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">API keys</h1>
        <p className="text-sm text-muted-foreground">
          Rotate and revoke per-client secrets. Plaintext is returned ONCE at rotation time — never
          retrievable after.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client</CardTitle>
          <CardDescription>Pick the partner whose secrets you want to manage.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} className="max-w-md">
            <option value="">Select a client…</option>
            {clientsQuery.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.clientId})
              </option>
            ))}
          </Select>
          {canManage && clientId && (
            <Button onClick={() => setRotateOpen(true)} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Rotate secret
            </Button>
          )}
        </CardContent>
      </Card>

      {clientId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Secrets</CardTitle>
            <CardDescription>
              Only the fingerprint is displayed — raw secrets are never stored server-side.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {secretsQuery.isLoading ? (
              <div className="space-y-2 p-6">
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : secretsQuery.isError ? (
              <div className="p-6 text-sm text-destructive">
                {(secretsQuery.error as Error)?.message ?? 'Could not load secrets.'}
              </div>
            ) : !secretsQuery.data || secretsQuery.data.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No secrets on file. Rotate to generate the first one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="p-3 font-medium">Fingerprint</th>
                      <th className="p-3 font-medium">Created</th>
                      <th className="p-3 font-medium">Expires</th>
                      <th className="p-3 font-medium">Status</th>
                      <th className="p-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {secretsQuery.data.map((s) => (
                      <tr key={s.id}>
                        <td className="p-3 font-mono text-xs">{s.hashedFingerprint}</td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {new Date(s.createdAt).toLocaleString()}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {s.expiresAt ? new Date(s.expiresAt).toLocaleString() : '—'}
                        </td>
                        <td className="p-3">
                          {s.isActive ? (
                            <Badge variant="success">Active</Badge>
                          ) : (
                            <Badge variant="outline">
                              Revoked
                              {s.revokedAt ? ` · ${new Date(s.revokedAt).toLocaleDateString()}` : ''}
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {canManage && s.isActive && (
                            <Button variant="ghost" size="sm" onClick={() => setRevokeTarget(s)}>
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {canManage && clientId && (
        <RotateSecretDialog clientId={clientId} open={rotateOpen} onOpenChange={setRotateOpen} />
      )}
      {canManage && clientId && revokeTarget && (
        <RevokeSecretDialog
          clientId={clientId}
          secret={revokeTarget}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </section>
  );
}

// ------ Rotate secret dialog (confirm → reveal plaintext ONCE) ------

function RotateSecretDialog({
  clientId,
  open,
  onOpenChange,
}: {
  clientId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState<RotateSecretResult | null>(null);
  const [visible, setVisible] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const mutation = useAdminSecretsPostAdminClientsIdGuidSecretsRotate({
    mutation: {
      onSuccess: async (result) => {
        setRevealed(result);
        await queryClient.invalidateQueries({
          queryKey: getAdminSecretsGetAdminClientsIdGuidSecretsQueryKey(clientId),
        });
      },
      onError: () => toast.error('Could not rotate secret.'),
    },
  });

  const close = () => {
    setRevealed(null);
    setVisible(false);
    setAcknowledged(false);
    setCopied(false);
    onOpenChange(false);
  };

  const copy = async () => {
    if (!revealed) return;
    await navigator.clipboard.writeText(revealed.plaintextSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && revealed && !acknowledged) return;
        if (!next) close();
        else onOpenChange(next);
      }}
    >
      <DialogContent>
        {!revealed ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-500" />
                Rotate secret
              </DialogTitle>
              <DialogDescription>
                Any active secret will be revoked immediately. Make sure the partner is ready to swap — in-flight
                HMAC calls will start failing.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button onClick={() => mutation.mutate({ id: clientId })} disabled={mutation.isPending}>
                {mutation.isPending ? 'Rotating…' : 'Rotate secret'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Save this secret — it won&apos;t be shown again</DialogTitle>
              <DialogDescription>Only displayed once. The hashed fingerprint is all we store.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted p-3">
                <code className="flex-1 break-all font-mono text-xs">
                  {visible ? revealed.plaintextSecret : '•'.repeat(revealed.plaintextSecret.length)}
                </code>
                <Button size="icon" variant="ghost" aria-label={visible ? 'Hide' : 'Show'} onClick={() => setVisible((v) => !v)}>
                  {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="icon" variant="ghost" aria-label="Copy" onClick={copy}>
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                I&apos;ve stored the secret in a secure vault.
              </label>
            </div>
            <DialogFooter>
              <Button onClick={close} disabled={!acknowledged}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------ Revoke confirm dialog ------

function RevokeSecretDialog({
  clientId,
  secret,
  onClose,
}: {
  clientId: string;
  secret: ClientSecretDto;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const mutation = useAdminSecretsDeleteAdminClientsIdGuidSecretsSecretIdGuid({
    mutation: {
      onSuccess: async () => {
        toast.success('Secret revoked.');
        await queryClient.invalidateQueries({
          queryKey: getAdminSecretsGetAdminClientsIdGuidSecretsQueryKey(clientId),
        });
        onClose();
      },
      onError: () => toast.error('Could not revoke secret.'),
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Revoke secret
          </DialogTitle>
          <DialogDescription>
            Secret{' '}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{secret.hashedFingerprint}</code>{' '}
            will stop authorising HMAC-signed calls immediately. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate({ id: clientId, secretId: secret.id })}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Revoking…' : 'Revoke secret'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
